// Builds table for one Chromium major. Lifted out of ChromiumMajorDetail.
//
// Renders the chromium binaries we've published for a given major and the
// fleet hosts that currently have each version installed. The "latest"
// badge is whatever `manifestMajor.latestBuild` resolves to — that's the
// version every fleet worker grabs on the next veil-update.sh tick.
//
// Operators see an Activate column that pins a specific ChromiumBuild row
// as `latestBuild` (POST /api/releases/chromium/:buildId/activate). Manifest
// rows that don't have a matching DB row (legacy on-disk webroot scan) get
// a dash in the action cell — there's nothing for the activate endpoint to
// pin to until someone re-publishes that version.
//
// Rendered with the shared @veil/ui DataTable stack; the version column
// sorts by semver.

import {
	type ColumnDef,
	getCoreRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	type SortingState,
	useReactTable,
} from "@tanstack/react-table";
import { compareSemver } from "@veil/shared/version";
import { Badge } from "@veil/ui/components/badge";
import { Button } from "@veil/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import { Skeleton } from "@veil/ui/components/skeleton";
import { DataTable, DataTableColumnHeader } from "@veil/ui/data-table";
import { formatBytes, formatRelative } from "@veil/ui/lib/format";
import { useMemo, useState } from "react";
import type {
	ChromiumBuildRow,
	FleetSnapshot,
	ManifestMajor,
} from "@/lib/releases/types.ts";
import { useChromiumBuildMutations } from "@/lib/releases/use-chromium-build-mutations.ts";

interface Props {
	major: string;
	manifestMajor: ManifestMajor | null | undefined;
	loading: boolean;
	fleetHosts: Array<{ hostname: string; snapshot: FleetSnapshot | null }>;
	// DB-tracked ChromiumBuild rows for this major. Used to resolve a
	// manifest version back to its build id (the activate endpoint takes
	// buildId, not version) and to read the authoritative isActive flag.
	builds?: ChromiumBuildRow[];
	// Operator role gate. Hides the Activate column entirely for viewers,
	// same posture as the profile-releases table.
	canPublish?: boolean;
}

interface ManifestBuildRow {
	version: string;
	releaseDate?: string;
	size: number;
}

export function PublishedBuildsTable({
	major,
	manifestMajor,
	loading,
	fleetHosts,
	builds,
	canPublish = false,
}: Props) {
	const { activate } = useChromiumBuildMutations();
	const [sorting, setSorting] = useState<SortingState>([
		{ id: "version", desc: true },
	]);

	const manifestBuilds = useMemo<ManifestBuildRow[]>(() => {
		if (!manifestMajor) return [];
		return Object.entries(manifestMajor.builds).map(([version, e]) => ({
			version,
			...e,
		}));
	}, [manifestMajor]);

	const hostsByBuild = useMemo(() => {
		const out = new Map<string, string[]>();
		for (const h of fleetHosts) {
			const versions = h.snapshot?.installedBuilds?.[major] ?? [];
			for (const v of versions) {
				if (!out.has(v)) out.set(v, []);
				const arr = out.get(v);
				if (arr && !arr.includes(h.hostname)) arr.push(h.hostname);
			}
		}
		return out;
	}, [fleetHosts, major]);

	// version -> DB row. Activate calls go through this map; if a manifest
	// version has no DB row (un-backfilled on-disk artifact) the activate
	// cell renders a dash because the canonical endpoint requires buildId.
	const buildByVersion = useMemo(() => {
		const out = new Map<string, ChromiumBuildRow>();
		for (const b of builds ?? []) out.set(b.fullVersion, b);
		return out;
	}, [builds]);

	const columns = useMemo<ColumnDef<ManifestBuildRow>[]>(() => {
		const cols: ColumnDef<ManifestBuildRow>[] = [
			{
				id: "version",
				accessorKey: "version",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Version" />
				),
				cell: ({ row }) => (
					<span className="font-mono text-xs">
						{row.original.version}
						{row.original.version === manifestMajor?.latestBuild ? (
							<Badge variant="success" className="ml-2">
								latest
							</Badge>
						) : null}
					</span>
				),
				sortingFn: (a, b) =>
					compareSemver(a.original.version, b.original.version),
				enableHiding: false,
			},
			{
				id: "released",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Released" />
				),
				accessorFn: (row) => row.releaseDate ?? "",
				cell: ({ row }) => (
					<span className="text-xs text-muted-foreground">
						{formatRelative(row.original.releaseDate)}
					</span>
				),
			},
			{
				id: "workers",
				header: "Workers",
				cell: ({ row }) => {
					const hosts = hostsByBuild.get(row.original.version) ?? [];
					if (hosts.length === 0) {
						return <span className="text-xs text-muted-foreground">—</span>;
					}
					return (
						<div className="flex flex-wrap gap-1">
							{hosts.map((h) => (
								<Badge
									key={h}
									variant="outline"
									className="font-mono text-[10px]"
								>
									{h}
								</Badge>
							))}
						</div>
					);
				},
				enableSorting: false,
			},
			{
				id: "size",
				accessorKey: "size",
				header: ({ column }) => (
					<DataTableColumnHeader
						column={column}
						title="Size"
						className="ml-auto"
					/>
				),
				cell: ({ row }) => (
					<div className="text-right font-mono tabular-nums text-xs">
						{formatBytes(row.original.size)}
					</div>
				),
			},
		];
		if (canPublish) {
			cols.push({
				id: "actions",
				header: () => <div className="text-right">Actions</div>,
				cell: ({ row }) => {
					const dbRow = buildByVersion.get(row.original.version);
					return (
						<div className="text-right">
							{dbRow ? (
								<Button
									size="sm"
									variant={dbRow.isActive ? "secondary" : "outline"}
									disabled={dbRow.isActive || activate.isPending}
									onClick={() =>
										activate.mutate({
											buildId: dbRow.id,
											version: dbRow.fullVersion,
										})
									}
								>
									{dbRow.isActive ? "Active" : "Activate"}
								</Button>
							) : (
								<span className="text-xs text-muted-foreground">—</span>
							)}
						</div>
					);
				},
				enableSorting: false,
				enableHiding: false,
			});
		}
		return cols;
	}, [
		manifestMajor?.latestBuild,
		hostsByBuild,
		buildByVersion,
		canPublish,
		activate,
	]);

	const table = useReactTable({
		data: manifestBuilds,
		columns,
		state: { sorting },
		onSortingChange: setSorting,
		getRowId: (row) => row.version,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		initialState: { pagination: { pageSize: 50 } },
	});

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-base">Published builds</CardTitle>
				<CardDescription>
					{manifestMajor?.latestBuild
						? `Latest: ${manifestMajor.latestBuild}`
						: "No latest build set"}
				</CardDescription>
			</CardHeader>
			<CardContent>
				{loading ? (
					<Skeleton className="h-24 w-full" />
				) : manifestBuilds.length === 0 ? (
					<div className="text-sm text-muted-foreground">
						No published builds for this major.
					</div>
				) : (
					<DataTable table={table} />
				)}
			</CardContent>
		</Card>
	);
}
